// Sales → Signature board. Stages mirror project.sales_stage, blockers mirror project.blocker.
//
// In-flight only. Won and lost are not stages here — they are the project's own `stage`: a won
// deal becomes "run", a lost one "dead", a quiet one "dormant", and all three drop off the board.
// See EXIT_STAGES below.
export const SALES_STAGES = [
  { value: "new", label: "New", color: "bg-gray-100 text-gray-800 border-gray-200" },
  { value: "discovery", label: "Discovery", color: "bg-blue-100 text-blue-800 border-blue-200" },
  { value: "proposal", label: "Proposal", color: "bg-yellow-100 text-yellow-800 border-yellow-200" },
  { value: "negotiation", label: "Negotiation", color: "bg-orange-100 text-orange-800 border-orange-200" },
  { value: "closing", label: "Closing", color: "bg-teal-100 text-teal-800 border-teal-200" },
];

// Closing a deal moves the project out of prospection, which is what takes it off the board.
export const EXIT_STAGES = [
  { value: "run", label: "Won", hint: "Becomes a running project", color: "bg-green-600 text-white hover:bg-green-700" },
  { value: "dead", label: "Lost", hint: "Marked dead", color: "bg-red-600 text-white hover:bg-red-700" },
  { value: "dormant", label: "Dormant", hint: "Parked, not lost — the state Jev flags on quiet deals", color: "bg-gray-600 text-white hover:bg-gray-700" },
];

export const BLOCKERS = [
  { value: "waiting_client", label: "Waiting on client", color: "bg-amber-100 text-amber-800" },
  { value: "needs_proposal", label: "Needs proposal", color: "bg-purple-100 text-purple-800" },
  { value: "needs_dev", label: "Needs dev availability", color: "bg-indigo-100 text-indigo-800" },
  { value: "needs_pricing", label: "Needs pricing", color: "bg-pink-100 text-pink-800" },
  { value: "needs_contract", label: "Needs contract", color: "bg-cyan-100 text-cyan-800" },
  { value: "other", label: "Other", color: "bg-gray-100 text-gray-800" },
];

export const stageLabel = (value) => SALES_STAGES.find((s) => s.value === value)?.label || "No stage";
// "2/5": how far down the funnel a deal is, so a stage reads as progress rather than a bare word.
export const stageStep = (value) => {
  const index = SALES_STAGES.findIndex((s) => s.value === value);
  return index === -1 ? "" : `${index + 1}/${SALES_STAGES.length}`;
};
export const stageColor = (value) => SALES_STAGES.find((s) => s.value === value)?.color || "bg-gray-100 text-gray-600 border-gray-200";
export const blockerLabel = (value) => BLOCKERS.find((b) => b.value === value)?.label || "";
export const blockerColor = (value) => BLOCKERS.find((b) => b.value === value)?.color || "bg-gray-100 text-gray-800";

export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "—";
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(amount);
};

export const formatDate = (date) => {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("fr-FR", { year: "numeric", month: "short", day: "numeric" });
};

// <input type="date"> wants yyyy-mm-dd.
export const toInputDate = (date) => (date ? new Date(date).toISOString().split("T")[0] : "");

// Noon local, so the calendar day survives toISOString in Europe. Used for "relance in N days".
export const addDaysInput = (days) => {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
};

// Someone marked the follow-up handled. The red Act stays off through the day before this date,
// then comes back on the morning it's due again.
export const isActionQuiet = (project) => {
  if (!project?.sales_action_quiet_until) return false;
  return toInputDate(project.sales_action_quiet_until) > addDaysInput(0);
};

export const isOverdue = (date) => !!date && new Date(date) < new Date();

// Amount comes from the project's budgets so the board agrees with the Pilotage pipe. Unsigned and
// not-lost budgets are the open pipeline; estimation_budget is the fallback before a budget exists.
export const dealAmount = (project, budgets = []) => {
  const open = budgets.filter((b) => !b.signed_at && !b.lost);
  const signed = budgets.filter((b) => b.signed_at);
  if (!open.length && !signed.length) return { amount: project.estimation_budget ?? null, weighted: null, estimated: true };

  const amount = open.reduce((sum, b) => sum + (b.amount || 0), 0) + signed.reduce((sum, b) => sum + (b.amount || 0), 0);
  const weighted = open.reduce((sum, b) => sum + ((b.amount || 0) * (b.confidence || 0)) / 100, 0) + signed.reduce((sum, b) => sum + (b.amount || 0), 0);
  return { amount, weighted, estimated: false };
};

// Jev scores the deal 0-4 against the rubric in api/src/utils/sales_scoring.js. The score is
// fractional, so the bands below are how a number becomes something readable on a row.
const URGENCY_BANDS = [
  { min: 3.5, label: "Act today", color: "bg-red-600 text-white" },
  { min: 2.5, label: "Drifting", color: "bg-orange-100 text-orange-800" },
  { min: 1.5, label: "Someone waiting", color: "bg-amber-100 text-amber-800" },
  { min: 0.5, label: "Routine", color: "bg-blue-100 text-blue-800" },
  { min: -1, label: "Calm", color: "bg-gray-100 text-gray-600" },
];

// Short names for the five rubric levels, in the same order as URGENCY_LEVELS in
// api/src/utils/sales_scoring.js. Duplicated rather than fetched: they are read-only labels for a
// chart, and a round trip to keep two constants in sync costs more than it's worth. Edit both.
export const URGENCY_LEVEL_LABELS = ["Nothing to do", "Routine follow-up", "Someone waiting on us", "Live deal drifting", "About to be lost"];

// Below this, the score is a guess. Those rows are the ones worth a human read rather than a sort.
export const isLowConfidence = (project) => project.sales_urgency_confidence < 0.6;

// A score is the mean of the distribution, so a deal split between "nothing to do" and "drifting
// badly" averages into a middle band that Jev actually ruled out. When confidence is low the band
// is an artifact, so say "unclear" rather than asserting a reading nobody holds.
export const urgencyBand = (project) => {
  if (project.sales_urgency === null || project.sales_urgency === undefined) return null;
  if (isLowConfidence(project)) return { label: "Unclear", color: "bg-gray-200 text-gray-700", unclear: true };
  return URGENCY_BANDS.find((b) => project.sales_urgency >= b.min);
};

// The rubric levels Jev actually weighted, most likely first. This is the closest thing to a reason
// the model gives us — it has no prose to offer, only where it put its probability mass.
export const urgencySplit = (project) => {
  const probabilities = project.sales_urgency_probabilities;
  if (!probabilities) return [];
  return Object.entries(probabilities)
    .map(([level, p]) => ({ level: Number(level), label: URGENCY_LEVEL_LABELS[Number(level)], p }))
    .sort((a, b) => b.p - a.p);
};

// Jev's read that the opportunity has gone quiet. Worth surfacing because half the board is months old.
export const isProbablyDead = (project) => project.sales_still_live !== undefined && project.sales_still_live < 0.5;

// Jev's answer to "does someone need to do something here in the next few days". Asked directly
// rather than read off the urgency score, because the score is a rubric position and this is a
// yes/no. Undefined on deals scored before the question existed, which read as no.
export const needsActionNow = (project) => !isActionQuiet(project) && project.sales_needs_action >= 0.5;

// "Act today" is the other red pill. A handled follow-up should not keep wearing it.
export const visibleUrgencyBand = (project) => {
  const band = urgencyBand(project);
  if (!band) return null;
  if (isActionQuiet(project) && band.label === "Act today") return null;
  return band;
};

// Live deals rank above dormant ones first, then by urgency inside each group. Sorting on urgency
// alone would float six-month-old records above deals in active conversation, since a stale deal
// reads as "drifting". Unscored deals keep the server's next_action_at order at the bottom.
export const byUrgency = (a, b) => {
  const liveA = isProbablyDead(a) ? 0 : 1;
  const liveB = isProbablyDead(b) ? 0 : 1;
  if (liveA !== liveB) return liveB - liveA;
  return (b.sales_urgency ?? -1) - (a.sales_urgency ?? -1);
};

// The "Needs action now" filter is a field check, so it returns missing dates alongside clients
// who have been waiting a fortnight. Jev's call goes first, then urgency inside each group. The
// deals with gaps stay on the list — they just stop sitting on top of the ones that matter.
export const byNeedsAction = (a, b) => {
  const actA = needsActionNow(a) ? 1 : 0;
  const actB = needsActionNow(b) ? 1 : 0;
  if (actA !== actB) return actB - actA;
  return byUrgency(a, b);
};

// The table reads hottest first: a live deal before a dormant one, then the closer to signature,
// then Jev's call and urgency inside each stage. The kanban keeps byNeedsAction, its columns
// already split by stage.
export const byHeat = (a, b) => {
  const liveA = isProbablyDead(a) ? 0 : 1;
  const liveB = isProbablyDead(b) ? 0 : 1;
  if (liveA !== liveB) return liveB - liveA;
  const stageA = SALES_STAGES.findIndex((s) => s.value === (a.sales_stage || "new"));
  const stageB = SALES_STAGES.findIndex((s) => s.value === (b.sales_stage || "new"));
  if (stageA !== stageB) return stageB - stageA;
  return byNeedsAction(a, b);
};

// Why a deal shows up in "Needs action now". Mirrors the needs_action $or in the project controller.
export const actionReasons = (project) => {
  if (["won", "lost"].includes(project.sales_stage)) return [];
  const reasons = [];
  if (!project.sales_owner_id) reasons.push("No owner");
  if (!project.next_action || !project.next_action_at) reasons.push("No next action");
  else if (isOverdue(project.next_action_at)) reasons.push("Overdue");
  if (project.blocker) reasons.push(blockerLabel(project.blocker));
  return reasons;
};
